import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const [courseId, setCourseId] = useState(null);
  const [isSub, setIsSub] = useState(false);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      if (attempts > 10) { setStatus("timeout"); return; }
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        setCourseId(res.data.course_id);
        setIsSub(!res.data.course_id);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          if (!res.data.course_id) { try { await refresh(); } catch (e) {} }
          return;
        }
        if (["failed", "expired"].includes(res.data.payment_status)) { setStatus("error"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        setStatus("error");
      }
    };
    poll();
  }, [params, refresh]);

  return (
    <div className="min-h-screen bg-background dot-grid flex items-center justify-center p-6">
      <div className="border border-border bg-card rounded-xl p-10 max-w-md w-full text-center" data-testid="payment-success-page">
        {status === "checking" && (
          <>
            <Loader2 size={36} className="mx-auto text-gold animate-spin" />
            <h1 className="font-serif text-2xl mt-5">Confirming…</h1>
            <p className="text-sm text-muted-foreground mt-2">One moment while we verify with Stripe.</p>
          </>
        )}
        {status === "paid" && isSub && (
          <>
            <Sparkles size={40} className="mx-auto text-cobalt" />
            <h1 className="font-serif text-3xl mt-5">Welcome to Ikigora Pro 💙</h1>
            <p className="text-sm text-muted-foreground mt-2">Thank you for chipping in. Everything is unlocked — premium spaces, courses and the Build studio.</p>
            <button onClick={() => navigate("/feed")} data-testid="payment-continue-button"
              className="mt-6 bg-cobalt text-white rounded-md px-6 py-3 text-sm hover:bg-gold-hover transition-colors">
              Explore the community
            </button>
          </>
        )}
        {status === "paid" && !isSub && (
          <>
            <CheckCircle2 size={40} className="mx-auto text-gold" />
            <h1 className="font-serif text-3xl mt-5">Access unlocked</h1>
            <p className="text-sm text-muted-foreground mt-2">The course is now yours for life.</p>
            <button onClick={() => navigate(courseId ? `/courses/${courseId}` : "/courses")}
              data-testid="payment-continue-button"
              className="mt-6 bg-gold text-accent-foreground rounded-md px-6 py-3 text-sm hover:bg-gold-hover transition-colors">
              Start learning
            </button>
          </>
        )}
        {(status === "error" || status === "timeout") && (
          <>
            <XCircle size={40} className="mx-auto text-destructive" />
            <h1 className="font-serif text-2xl mt-5">Something went wrong</h1>
            <p className="text-sm text-muted-foreground mt-2">
              {status === "timeout" ? "Still processing — check back shortly." : "We couldn't confirm the payment."}
            </p>
            <button onClick={() => navigate("/feed")}
              className="mt-6 border border-border px-6 py-3 text-sm hover:bg-secondary/50 transition-colors">
              Back to Ikigora
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background dot-grid flex items-center justify-center p-6">
      <div className="border border-border bg-card rounded-xl p-10 max-w-md w-full text-center" data-testid="payment-cancel-page">
        <XCircle size={40} className="mx-auto text-muted-foreground" />
        <h1 className="font-serif text-2xl mt-5">Checkout cancelled</h1>
        <p className="text-sm text-muted-foreground mt-2">No charge was made. You can purchase anytime.</p>
        <button onClick={() => navigate("/courses")}
          className="mt-6 border border-border px-6 py-3 text-sm hover:bg-secondary/50 transition-colors">
          Back to courses
        </button>
      </div>
    </div>
  );
}
